import { Text, Banner, Switch, Box, Flex } from '@sequencehq/core-components'
import {
  GreyGrey80,
  GreyGrey90,
  Primary13Regular,
  Primary14SemiBold
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { useCreditNoteQuery } from 'CreditNotes/hooks/useCreditNote.ts'
import { CREDIT_NOTE_STATUSES } from 'CreditNotes/constants'
import { useCreditNoteActions } from 'CreditNotes/hooks/useCreditNoteActions'
import { SendConfirmationModal } from 'common/components/SteppedModal/SendConfirmationModal'
import { useAccountingLockDateValidation } from 'common/components/SteppedModal/useAccountingLockDateValidation'
import { useMissingCustomerAddressToast } from 'common/hooks'
import { useEffect, useRef } from 'react'

const CONFIRM_TITLE_TEXT = 'Issue and send'

type Props = {
  creditNoteId: string
  onClose: () => void
}

export const CreditNoteIssueDialog = ({ creditNoteId, onClose }: Props) => {
  const { data: creditNote } = useCreditNoteQuery(creditNoteId)
  const showMissingAddressToast = useMissingCustomerAddressToast()
  const reportedMissingAddress = useRef(false)

  // The backend rejects the send without a billing address, so there is
  // nothing to confirm. Say why and close, rather than offering a dialog whose
  // only outcome is a failure that doesn't name the cause.
  const isMissingBillingAddress =
    Boolean(creditNote) && !creditNote?.customerBillingAddress

  useEffect(() => {
    if (!isMissingBillingAddress || reportedMissingAddress.current) {
      return
    }

    reportedMissingAddress.current = true
    showMissingAddressToast('sending this credit note')
    onClose()
  }, [isMissingBillingAddress, showMissingAddressToast, onClose])

  if (!creditNote || isMissingBillingAddress) {
    return null
  }

  return (
    <CreditNoteIssueConfirmation
      creditNoteId={creditNoteId}
      creditNote={creditNote}
      onClose={onClose}
    />
  )
}

const CreditNoteIssueConfirmation = ({
  creditNoteId,
  creditNote,
  onClose
}: {
  creditNoteId: string
  creditNote: NonNullable<ReturnType<typeof useCreditNoteQuery>['data']>
  onClose: () => void
}) => {
  const { issueCreditNote, grantCreditAction } =
    useCreditNoteActions(creditNoteId)

  const isDraft = creditNote.status === CREDIT_NOTE_STATUSES.DRAFT

  const showGrantCredits = isDraft && creditNote.currency && creditNote.netTotal

  const handleConfirm = async () => {
    try {
      await issueCreditNote.mutateAsync()
    } catch {
      return
    }

    onClose()
  }

  const lockDate = useAccountingLockDateValidation({
    entityName: 'credit note',
    // Match backend enforcement (CreditNoteValidator): it locks against the
    // accounting date, falling back to the issue date when absent.
    entityDate: creditNote.accountingDate ?? creditNote.issueDate,
    blockedTitle: "This credit note can't be sent",
    onClose
  })

  return (
    <SendConfirmationModal
      onClose={onClose}
      onConfirm={handleConfirm}
      submitting={issueCreditNote.isLoading}
      submitDisabled={grantCreditAction.isLoading}
      loading={lockDate.loading}
      blocker={lockDate.blocker}
      validation={lockDate.validation}
      validationSummaryText="There are a few things to sort out before you can send this credit note."
      confirmationSummaryText={
        <>
          <Text {...Primary13Regular} color={GreyGrey80}>
            Issue and send this credit note to{' '}
            <b>{creditNote.customerLegalCompanyName}</b> (
            {creditNote.customerEmails.join(', ')})? After sending you can no
            longer edit the credit note.
          </Text>
          {showGrantCredits && (
            <Box paddingTop={4}>
              <Banner variant="neutral" withIcon={false}>
                <Flex flexDirection="column" gap={2}>
                  <Switch
                    onChange={grantCreditAction.mutate}
                    defaultChecked={
                      creditNote.settings.generateCashCreditGrant === 'ENABLED'
                    }
                    disabled={issueCreditNote.isLoading}
                  >
                    <Text {...Primary14SemiBold} color={GreyGrey90}>
                      Add as cash credit
                    </Text>
                  </Switch>
                  <Text {...Primary13Regular} color={GreyGrey80}>
                    Creates a{' '}
                    {toMoney({
                      currency: creditNote.currency,
                      value: creditNote.grossTotal.toString()
                    })}{' '}
                    cash credit grant for{' '}
                    <b>{creditNote.customerLegalCompanyName}</b>. Available
                    credit is automatically applied to their future invoices.
                  </Text>
                </Flex>
              </Banner>
            </Box>
          )}
        </>
      }
      confirmTitleText={CONFIRM_TITLE_TEXT}
      confirmActionText="Issue and send"
    />
  )
}
